import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowUpRight, Linkedin, Instagram, Facebook, Cookie, ShieldCheck } from 'lucide-react';
import { NAV_LINKS, CONTACT_INFO, LEGAL_INFO } from '../data/mock';
import { useLang } from '../context/UIContext';

export default function Footer() {
  const { t } = useLang();
  const C = LEGAL_INFO.controller;

  const openCookiePrefs = () => {
    window.dispatchEvent(new CustomEvent('cookie-consent-reopen'));
  };

  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/8 bg-[#080e26] pt-20 pb-8 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid md:grid-cols-4 gap-12 mb-14">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/dts-brain.png" alt="Digital Tech Solutions" className="w-14 h-14 object-contain" />
              <span className="font-display font-bold text-white">Digital Tech Solutions</span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-5">
              {t('footer.tagline', 'Un ecosistema che unisce tecnologia, persone e sostenibilita. Costruiamo connessioni digitali che generano valore reale.')}
            </p>
            <div className="flex gap-3">
              {[Linkedin, Instagram, Facebook].map((Icon, i) => (
                <a key={i} href="#" className="w-10 h-10 rounded-full border border-white/12 flex items-center justify-center text-white/60 hover:border-lime hover:text-lime transition-colors">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-mono-t text-lime text-xs mb-5">_{t('footer.navigation', 'navigazione')}</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}><Link to={l.path} className="text-white/60 text-sm hover:text-lime transition-colors">{t(l.key, l.label)}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono-t text-lime text-xs mb-5">_academy</h4>
            <ul className="space-y-3">
              <li><Link to="/academy/catalogo" className="text-white/60 text-sm hover:text-lime transition-colors">{t('footer.catalog', 'Catalogo Corsi')}</Link></li>
              <li><Link to="/academy/login" className="text-white/60 text-sm hover:text-lime transition-colors">{t('footer.login', 'Accedi')}</Link></li>
              <li><Link to="/acdt" className="text-white/60 text-sm hover:text-lime transition-colors">{t('footer.acdt_cert', 'Certificazione ACDT')}</Link></li>
              <li><Link to="/rinnovi" className="text-white/60 text-sm hover:text-lime transition-colors">{t('footer.renewals', 'Rinnovi tessera e registro')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono-t text-lime text-xs mb-5">_{t('footer.contact', 'contatti')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-white/60 text-sm"><MapPin size={16} className="text-lime mt-0.5 shrink-0" /> {CONTACT_INFO.address}</li>
              <li className="flex items-center gap-3 text-white/60 text-sm"><Phone size={16} className="text-lime shrink-0" /> {CONTACT_INFO.phone}</li>
              <li className="flex items-center gap-3 text-white/60 text-sm"><Mail size={16} className="text-lime shrink-0" /> <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-lime">{CONTACT_INFO.email}</a></li>
              <li className="flex items-center gap-3 text-white/60 text-sm"><ShieldCheck size={16} className="text-lime shrink-0" /> <a href={`mailto:${CONTACT_INFO.pec}`} className="hover:text-lime">{CONTACT_INFO.pec}</a> <span className="text-white/35 text-xs">(PEC)</span></li>
            </ul>
            <Link to="/contatti" className="mt-5 inline-flex items-center gap-1 text-lime text-sm font-semibold hover:gap-2 transition-all">
              {t('footer.cta_consult', 'Prenota una consulenza')} <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>

        {/* Legal company data strip */}
        <div className="pt-6 pb-5 border-t border-white/8 text-white/45 text-xs leading-relaxed space-y-1" data-testid="footer-legal-info">
          <p>
            <strong className="text-white/70">{C.name}</strong> · {t('footer.owner_label', 'Titolare')}: {C.owner} · {t('footer.vat', 'P.IVA / C.F.')} {C.vat}{C.rea ? ` · REA ${C.rea}` : ''}
          </p>
          <p>
            {t('footer.hq', 'Sede')}: {C.address} · Email <a className="hover:text-lime" href={`mailto:${C.email}`}>{C.email}</a> · PEC <a className="hover:text-lime" href={`mailto:${C.pec}`}>{C.pec}</a> · Tel <a className="hover:text-lime" href={`tel:${C.phone.replace(/\s/g, '')}`}>{C.phone}</a>
          </p>
        </div>

        <div className="pt-6 border-t border-white/8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs">© {year} Digital Tech Solutions. {t('footer.rights', 'Tutti i diritti riservati.')}</p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/privacy-policy" className="text-white/40 text-xs hover:text-lime transition-colors">{t('footer.privacy', 'Privacy Policy')}</Link>
            <Link to="/cookie-policy" className="text-white/40 text-xs hover:text-lime transition-colors">{t('footer.cookie_policy', 'Cookie Policy')}</Link>
            <Link to="/termini" className="text-white/40 text-xs hover:text-lime transition-colors">{t('footer.terms', 'Termini')}</Link>
            <button
              type="button"
              onClick={openCookiePrefs}
              data-testid="footer-cookie-prefs"
              className="inline-flex items-center gap-1 text-white/40 text-xs hover:text-lime transition-colors"
            >
              <Cookie size={12} /> {t('footer.cookie_prefs', 'Modifica preferenze cookie')}
            </button>
            <a href={LEGAL_INFO.adrOdr} target="_blank" rel="noreferrer" className="text-white/40 text-xs hover:text-lime transition-colors">
              ODR UE
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
